import React, { useEffect } from "react";
import { Button, Form, Icon, Grid, Card, Table } from "tabler-react";
import Select from "react-select";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useWhatsappTramos, emptyTramo } from "../hooks/useWhatsappTramos";

const errStyle = { color: "#cd201f", fontSize: "11px", marginTop: "3px" };

const validateTime = (val) => {
  if (!val || val.includes("_")) return "Ingresa una hora válida (HH:MM:SS)";
  const [h, m, s] = val.split(":").map(Number);
  if (h > 23) return "Las horas deben ser 00–23";
  if (m > 59) return "Los minutos deben ser 00–59";
  if (s > 59) return "Los segundos deben ser 00–59";
  if (h === 0 && m === 0 && s === 0) return "La hora de envío debe ser mayor a 00:00:00";
  return true;
};

const ConfigWhatsappTramos = ({ idCliente, idPlataforma }) => {
  const {
    tramos, movimientosTipo, camposLogisticos,
    editando, editandoDefaultValues, cargando,
    cargarCamposPorMovimiento, guardarTramo, editarTramo, borrarTramo, resetForm,
    getMovimientoLabel, getCampoLabel,
  } = useWhatsappTramos(idCliente, idPlataforma);

  const {
    handleSubmit, control, reset, setValue, trigger,
    formState: { errors },
  } = useForm({ defaultValues: emptyTramo, mode: "onChange" });

  // Poblar el formulario al entrar en modo edición o limpiarlo al cancelar
  useEffect(() => {
    reset(editandoDefaultValues);
  }, [editando]); // eslint-disable-line react-hooks/exhaustive-deps

  // Valores reactivos para filtrar opciones inicio/fin entre sí
  const watchInicio = useWatch({ control, name: "inicio" });
  const watchFin = useWatch({ control, name: "fin" });

  const onSubmit = (data) => guardarTramo(data);

  const onCancelar = () => {
    resetForm();
    reset(emptyTramo);
  };

  return (
    <div>
      <Card>
        <Card.Header>
          <Card.Title>
            <Icon name={editando ? "edit-2" : "plus-circle"} className="mr-2" />
            {editando ? "Editar tramo" : "Nuevo tramo"}
          </Card.Title>
          {editando && (
            <Card.Options>
              <Button size="sm" color="secondary" outline onClick={onCancelar}>
                <Icon name="x" /> Cancelar
              </Button>
            </Card.Options>
          )}
        </Card.Header>
        <Card.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid.Row>
              <Grid.Col md={4}>
                <Form.Group label="Tipo de movimiento">
                  <Controller
                    name="idMovimientoTipo"
                    control={control}
                    rules={{ validate: v => v?.value > 0 || "Selecciona un tipo de movimiento" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={movimientosTipo}
                        placeholder="Selecciona..."
                        onChange={(e) => {
                          field.onChange(e);
                          setValue("inicio", emptyTramo.inicio, { shouldValidate: false });
                          setValue("fin", emptyTramo.fin, { shouldValidate: false });
                          cargarCamposPorMovimiento(e?.value);
                        }}
                      />
                    )}
                  />
                  {errors.idMovimientoTipo && (
                    <div style={errStyle}>{errors.idMovimientoTipo.message}</div>
                  )}
                </Form.Group>
              </Grid.Col>

              <Grid.Col md={4}>
                <Form.Group label="Nombre del tramo">
                  <Controller
                    name="nombre"
                    control={control}
                    rules={{
                      required: "El nombre es obligatorio",
                      validate: v => v.trim().length > 0 || "El nombre no puede estar vacío",
                      maxLength: { value: 100, message: "Máximo 100 caracteres" },
                    }}
                    render={({ field }) => (
                      <input
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        className={`form-control${errors.nombre ? " is-invalid" : ""}`}
                        placeholder="Ej. Ingreso → Salida cliente"
                        maxLength="100"
                      />
                    )}
                  />
                  {errors.nombre && <div style={errStyle}>{errors.nombre.message}</div>}
                </Form.Group>
              </Grid.Col>

              <Grid.Col md={2}>
                <Form.Group label="Hora recurrente">
                  <Controller
                    name="frecuenciaMinutos"
                    control={control}
                    rules={{ validate: validateTime }}
                    render={({ field }) => (
                      <Form.MaskedInput
                        {...field}
                        placeholder="HH:MM:SS"
                        mask={[/\d/, /\d/, ":", /\d/, /\d/, ":", /\d/, /\d/]}
                      />
                    )}
                  />
                  {errors.frecuenciaMinutos && (
                    <div style={errStyle}>{errors.frecuenciaMinutos.message}</div>
                  )}
                </Form.Group>
              </Grid.Col>

              <Grid.Col md={2} style={{ display: "flex", alignItems: "center", paddingTop: "28px" }}>
                <Controller
                  name="activo"
                  control={control}
                  render={({ field }) => (
                    <Form.Switch
                      label="Activo"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              </Grid.Col>
            </Grid.Row>

            <Grid.Row>
              <Grid.Col md={4}>
                <Form.Group label="Campo inicio">
                  <Controller
                    name="inicio"
                    control={control}
                    rules={{
                      validate: {
                        required: v => v?.value > 0 || "Selecciona el campo de inicio",
                        orden: (v, form) => {
                          const fin = form.fin;
                          if (!v?.value || !fin?.value) return true;
                          if (v.orden === undefined || fin.orden === undefined) return true;
                          return v.orden < fin.orden || "El campo inicio debe ocurrir antes que el campo fin";
                        },
                      },
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={camposLogisticos.filter(c => c.value !== watchFin?.value)}
                        placeholder="Selecciona campo..."
                        isLoading={camposLogisticos.length === 0}
                        onChange={(e) => { field.onChange(e); trigger("fin"); }}
                      />
                    )}
                  />
                  {errors.inicio && <div style={errStyle}>{errors.inicio.message}</div>}
                </Form.Group>
              </Grid.Col>

              <Grid.Col md={1} style={{ display: "flex", alignItems: "center", paddingTop: "28px", justifyContent: "center" }}>
                <Icon name="arrow-right" style={{ color: "#9aa0ac", fontSize: "18px" }} />
              </Grid.Col>

              <Grid.Col md={4}>
                <Form.Group label="Campo fin">
                  <Controller
                    name="fin"
                    control={control}
                    rules={{
                      validate: {
                        required: v => v?.value > 0 || "Selecciona el campo de fin",
                        orden: (v, form) => {
                          const inicio = form.inicio;
                          if (!v?.value || !inicio?.value) return true;
                          if (v.orden === undefined || inicio.orden === undefined) return true;
                          return v.orden > inicio.orden || "El campo fin debe ocurrir después del campo inicio";
                        },
                      },
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={camposLogisticos.filter(c => c.value !== watchInicio?.value)}
                        placeholder="Selecciona campo..."
                        isLoading={camposLogisticos.length === 0}
                        onChange={(e) => { field.onChange(e); trigger("inicio"); }}
                      />
                    )}
                  />
                  {errors.fin && <div style={errStyle}>{errors.fin.message}</div>}
                </Form.Group>
              </Grid.Col>

              <Grid.Col md={3} style={{ display: "flex", alignItems: "flex-end", paddingBottom: "4px" }}>
                <Button
                  type="submit"
                  color={editando ? "success" : "primary"}
                  pill size="sm" block
                >
                  <Icon name={editando ? "check" : "plus"} />
                  {" "}{editando ? "Actualizar tramo" : "Agregar tramo"}
                </Button>
              </Grid.Col>
            </Grid.Row>
          </form>
        </Card.Body>
      </Card>

      <div style={{ marginTop: "16px" }}>
        <p style={{
          fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.08em", color: "#6c757d",
          borderBottom: "2px solid #e9ecef", paddingBottom: "6px", marginBottom: "12px",
        }}>
          <Icon name="list" className="mr-1" /> Tramos configurados ({tramos.length})
        </p>
        {cargando ? (
          <div style={{ textAlign: "center", padding: "32px 16px", color: "#9aa0ac" }}>
            Cargando tramos...
          </div>
        ) : tramos.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "32px 16px", color: "#9aa0ac",
            background: "#f8f9fa", borderRadius: "8px", border: "1px dashed #dee2e6",
          }}>
            <Icon name="inbox" style={{ fontSize: "28px", marginBottom: "8px", display: "block" }} />
            <div style={{ fontSize: "13px" }}>Sin tramos configurados. Agrega el primero arriba.</div>
          </div>
        ) : (
          <Table cards striped responsive className="table-vcenter table-sm">
            <Table.Header>
              <tr>
                <th>Tipo movimiento</th>
                <th>Nombre tramo</th>
                <th style={{ textAlign: "center" }}>Campo inicio</th>
                <th style={{ textAlign: "center" }}></th>
                <th style={{ textAlign: "center" }}>Campo fin</th>
                <th style={{ textAlign: "center" }}>Hora recurrente</th>
                <th style={{ textAlign: "center" }}>Estatus</th>
                <th style={{ textAlign: "center" }}>Acciones</th>
              </tr>
            </Table.Header>
            <Table.Body>
              {tramos.map((t, i) => (
                <Table.Row
                  key={t.idConfig ?? t.id ?? i}
                  style={{
                    background:
                      (editando?.idConfig ?? editando?.id) === (t.idConfig ?? t.id)
                        ? "#eef4ff"
                        : undefined,
                  }}
                >
                  <Table.Col>
                    <span style={{ fontWeight: 600, fontSize: "12px" }}>
                      {getMovimientoLabel(t.idMovimientoTipo)}
                    </span>
                  </Table.Col>
                  <Table.Col>
                    <span style={{ fontSize: "13px" }}>{t.nombre}</span>
                  </Table.Col>
                  <Table.Col style={{ textAlign: "center" }}>
                    <span style={{
                      background: "#e8f4fd", color: "#2c7be5",
                      padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
                    }}>
                      {getCampoLabel(t.inicio)}
                    </span>
                  </Table.Col>
                  <Table.Col style={{ textAlign: "center", color: "#9aa0ac" }}>
                    <Icon name="arrow-right" />
                  </Table.Col>
                  <Table.Col style={{ textAlign: "center" }}>
                    <span style={{
                      background: "#e6f9f0", color: "#0f9960",
                      padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
                    }}>
                      {getCampoLabel(t.fin)}
                    </span>
                  </Table.Col>
                  <Table.Col style={{ textAlign: "center" }}>
                    <span style={{
                      background: "#f3f0ff", color: "#6f42c1",
                      padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
                      fontFamily: "monospace",
                    }}>
                      {t.frecuenciaMinutos || "—"}
                    </span>
                  </Table.Col>
                  <Table.Col style={{ textAlign: "center" }}>
                    <span className={`badge badge-${t.activo ? "success" : "secondary"}`}>
                      {t.activo ? "Activo" : "Inactivo"}
                    </span>
                  </Table.Col>
                  <Table.Col style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                    <Button
                      size="sm" color="secondary" outline
                      onClick={() => editarTramo(t)}
                      style={{ marginRight: "4px" }}
                    >
                      <Icon name="edit" />
                    </Button>
                    {/* <Button size="sm" color="danger" outline onClick={() => borrarTramo(t)}>
                      <Icon name="trash" />
                    </Button> */}
                  </Table.Col>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </div>
  );
};

export default ConfigWhatsappTramos;
